import React, { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { findMovieById } from "../services/movieService"
import { listScenes } from "../services/sceneService"
import { useAuth } from "../context/AuthContext"
import EmotionBarcode from "./EmotionBarcode"
import "./MovieProfile.css"

const EMOTION_COLORS = {
  happy:"#E7C239", amused:"#D93D57", involved:"#E34327", inspired:"#EC9965", tender:"#E65C9E",
  surprise:"#53A7BD", astonished:"#59A8C9", sad:"#3374B4", melancholic:"#3374B4", bored:"#95B6D8",
  compassioned:"#A8D4EA", fear:"#5BAF61", disturbed:"#418266", scared:"#428249", disgust:"#7556A5",
  disappointed:"#9678B6", embarrassed:"#3374B4", angry:"#975767", annoyed:"#D096A2", nothing:"#333333",
}

const fmt = (sec) => {
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}m${String(s).padStart(2, "0")}s`
}

const MovieProfile = () => {
  const { id } = useParams()
  const { isAuthenticated } = useAuth()
  const [movie, setMovie]     = useState(null)
  const [scenes, setScenes]   = useState([])
  const [hoveredScene, setHoveredScene] = useState(null)

  useEffect(() => {
    findMovieById(id).then(setMovie).catch(console.error)
    listScenes(id).then(setScenes).catch(console.error)
  }, [id])

  if (!movie) return <div className="mp-loading">Loading…</div>

  const durationSec = (movie.duration || 0) * 60
  const maxEnd = scenes.reduce((m, s) => Math.max(m, s.endSec), durationSec || 1) || 1
  const dominantColor = EMOTION_COLORS[movie.emotion?.[0]] || "#cfc8bf"

  const tickCount  = Math.min(13, Math.floor((movie.duration || 0) / 10) + 1)
  const tickValues = Array.from({ length: tickCount }, (_, i) =>
    Math.round((i / Math.max(tickCount - 1, 1)) * (movie.duration || 0))
  )

  return (
    <div className="mp-page">
      <div className="mp-layout">

        {/* ── poster ── */}
        <div className="mp-poster-col">
          <img
            src={`https://image.tmdb.org/t/p/w500/${movie.poster_path}`}
            alt={movie.title}
            className="mp-poster"
          />
        </div>

        {/* ── info + timeline ── */}
        <div className="mp-content-col">

          <div className="mp-info">
            <h1 className="mp-title">{movie.title}</h1>

            <div className="mp-details">
              {movie.director    && <p><span className="mp-label">Director</span>{movie.director}</p>}
              {movie.genre       && <p><span className="mp-label">Genre</span>{movie.genre}</p>}
              {movie.country     && <p><span className="mp-label">Country</span>{movie.country}</p>}
              {movie.duration    && <p><span className="mp-label">Duration</span>{movie.duration} min</p>}
              {movie.vote_average && <p><span className="mp-label">Rating</span>{movie.vote_average} / 10</p>}
            </div>

            {movie.overview && (
              <p className="mp-overview">{movie.overview}</p>
            )}

            <div className="mp-actions">
              <span
                className="mp-emotion-badge"
                style={{ background: dominantColor }}
              >
                {movie.emotion?.[0] || "nothing"}
              </span>
              <Link to={`/classify/${id}`} className="ifelt-button">
                Classify
              </Link>
            </div>
          </div>

          {/* ── emotion barcode ── */}
          <div className="mp-barcode-wrap">
            <div className="mp-timeline-label">Emotion Palette</div>
            <EmotionBarcode
              emotion={movie.emotion?.[0]}
              summary={movie.emotionSummary}
              seed={id}
            />
          </div>

          {/* ── timeline ── */}
          <div className="mp-timeline-wrap">
            <div className="mp-timeline-label">
              {isAuthenticated ? "Global Timeline" : "Emotional Timeline"}
              <span className="mp-timeline-count">
                {scenes.length} {scenes.length === 1 ? "scene" : "scenes"}
              </span>
            </div>

            <div className="mp-bar">
              {scenes.length === 0 && (
                <div className="mp-bar__base" style={{ background: dominantColor }} />
              )}
              {scenes.map((s) => {
                const left  = (s.startSec / maxEnd) * 100
                const width = ((s.endSec - s.startSec) / maxEnd) * 100
                return (
                  <div
                    key={s._id}
                    className="mp-seg"
                    style={{
                      left:   `${left}%`,
                      width:  `${width}%`,
                      background: EMOTION_COLORS[s.emotion] || "#333",
                      filter: `saturate(${(s.intensity / 5) * 100}%)`,
                    }}
                    onMouseEnter={() => setHoveredScene({
                      id: s._id,
                      label: s.emotion,
                      color: EMOTION_COLORS[s.emotion] || "#333",
                      videoId: s.youtubeVideoId && s.youtubeVideoId !== "none" ? s.youtubeVideoId : null,
                      midFrac: (s.startSec + s.endSec) / 2 / maxEnd,
                    })}
                    onMouseLeave={() => setHoveredScene(null)}
                  />
                )
              })}

              {hoveredScene && (
                <div
                  className="mp-hover-pop"
                  style={{ left: `clamp(110px, ${hoveredScene.midFrac * 100}%, calc(100% - 110px))` }}
                >
                {hoveredScene.videoId && (
                  <div className="mp-hover-thumb">
                    <img
                      src={`https://img.youtube.com/vi/${hoveredScene.videoId}/hqdefault.jpg`}
                      alt={hoveredScene.label}
                    />
                    <span className="mp-hover-play">▶</span>
                  </div>
                )}
                <span className="mp-hover-label" style={{ color: hoveredScene.color }}>
                  {hoveredScene.label}
                </span>
                </div>
              )}
            </div>

            <div className="mp-ticks">
              {tickValues.map((min) => (
                <div
                  key={min}
                  className="mp-tick"
                  style={{ left: `${(min / (movie.duration || 1)) * 100}%` }}
                >
                  <span>{min}</span>
                </div>
              ))}
              {movie.duration > 0 && <span className="mp-tick-unit">MIN</span>}
            </div>

            {scenes.length === 0 && (
              <p className="mp-hint">
                No classified scenes yet.{" "}
                <Link to={`/classify/${id}`}>Classify now →</Link>
              </p>
            )}

            {/* ── your own timeline (logged-in only) ── */}
            {isAuthenticated && (() => {
              const mine = scenes.filter((s) => s.mine)
              return (
                <div className="mp-mine">
                  <div className="mp-timeline-label">
                    Your Timeline
                    <span className="mp-timeline-count">
                      {mine.length} {mine.length === 1 ? "scene" : "scenes"}
                    </span>
                  </div>
                  <div className="mp-bar">
                    {mine.length === 0 ? (
                      <div className="mp-bar__base mp-bar__base--empty" />
                    ) : (
                      mine.map((s) => (
                        <div
                          key={s._id}
                          className="mp-seg"
                          style={{
                            left:  `${(s.startSec / maxEnd) * 100}%`,
                            width: `${((s.endSec - s.startSec) / maxEnd) * 100}%`,
                            background: EMOTION_COLORS[s.emotion] || "#333",
                            filter: `saturate(${(s.intensity / 5) * 100}%)`,
                          }}
                          title={s.emotion}
                        />
                      ))
                    )}
                  </div>
                  {mine.length === 0 && (
                    <p className="mp-hint">
                      You haven't classified this film yet.{" "}
                      <Link to={`/classify/${id}`}>Add your scenes →</Link>
                    </p>
                  )}
                </div>
              )
            })()}
          </div>


        </div>
      </div>
    </div>
  )
}

export default MovieProfile
